import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import useEinvoicing from "services/hooks/useEinvoicing";
import { withoutSymbolFormatter } from "utilities/currencyFormatter";

const money = (value) => `UGX ${withoutSymbolFormatter(Number(value || 0))}`;

const STATUS_COLORS = {
  not_started: "default",
  keys_generated: "info",
  awaiting_device: "warning",
  verified: "info",
  products_registered: "info",
  active: "success",
  error: "error",
};

/**
 * EFRIS for the landlord portal: explains, onboards (self-service) and, once
 * active, lists the landlord's EFRIS documents and issues rent invoices.
 */
export default function EfrisPanel() {
  const { getStatus } = useEinvoicing();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const result = await getStatus();
    if (result.success) setStatus(result.data.data);
    else setError(result.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!status) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!status.einvoicing_enabled) {
    return (
      <Alert severity="info">
        <strong>You aren't enabled for EFRIS invoicing yet.</strong> With EFRIS through RentBeta,
        your tenants get official URA receipts and invoices in your name, generated automatically.
        Contact RentBeta or your property manager to switch it on.
      </Alert>
    );
  }
  const onboarding = status.onboarding;
  if (onboarding?.onboarding_status !== "active") {
    return <SelfOnboarding config={onboarding} onChanged={load} />;
  }
  return <EfrisDocuments config={onboarding} />;
}

function SelfOnboarding({ config: initial, onChanged }) {
  const {
    generateKeys,
    saveDevice,
    verifyConnection,
    downloadPublicKey,
    downloadInstructions,
  } = useEinvoicing();
  const [config, setConfig] = useState(initial);
  const [device, setDevice] = useState({ tin: initial?.tin || "", device_no: initial?.device_no || "" });
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const apply = (result, successMessage) => {
    if (result.data?.data) setConfig(result.data.data);
    setFeedback(
      result.success
        ? { severity: "success", message: successMessage }
        : { severity: "error", message: result.message }
    );
    return result;
  };

  const step = async (fn, successMessage) => {
    setBusy(true);
    const result = apply(await fn(), successMessage);
    setBusy(false);
    return result;
  };

  const handleGenerate = () => {
    let force = false;
    if (config?.has_private_key) {
      // eslint-disable-next-line no-alert
      if (!window.confirm("You already have a key. A new key replaces the one uploaded on EFRIS, so you'd need to upload it again. Continue?")) return;
      force = true;
    }
    step(() => generateKeys(force), "Your key is ready. Download it in step 2.");
  };

  const handleDownloadKey = async (type) => {
    const result = await downloadPublicKey(type);
    if (!result.success) setFeedback({ severity: "error", message: result.message });
  };

  const handleDownloadInstructions = async () => {
    const result = await downloadInstructions();
    if (!result.success) setFeedback({ severity: "error", message: result.message });
  };

  const handleVerify = async () => {
    const saved = await step(
      () => saveDevice({ tin: device.tin, device_no: device.device_no }),
      "Details saved."
    );
    if (!saved.success) return;
    const result = await step(
      verifyConnection,
      "Connected! EFRIS receipts will now be issued in your name."
    );
    if (result.success) onChanged?.();
  };

  const hasKeys = Boolean(config?.has_private_key);
  const activeStep = !hasKeys ? 0 : !config?.device_no ? 1 : 2;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight="bold">
          Set up EFRIS receipts
        </Typography>
        {config && (
          <Chip
            label={config.onboarding_status_label}
            color={STATUS_COLORS[config.onboarding_status] || "default"}
          />
        )}
      </Stack>
      <Typography variant="body2" sx={{ mb: 2 }}>
        RentBeta can issue URA EFRIS receipts and invoices for your rent, with <strong>your name
        and TIN</strong> on them. You do this setup once; it takes about 10 minutes. We never ask
        for your URA password.
      </Typography>
      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}
      {config?.onboarding_status === "error" && config?.onboarding_error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Last attempt failed: {config.onboarding_error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        <Step expanded completed={hasKeys}>
          <StepLabel>Create your RentBeta key</StepLabel>
          <StepContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This creates a secure key pair for your EFRIS connection. The secret part stays
              locked on RentBeta's server; you only get the public part to upload to URA.
            </Typography>
            <Button variant="outlined" disabled={busy} onClick={handleGenerate}>
              {hasKeys ? "Create a new key" : "Create my key"}
            </Button>
          </StepContent>
        </Step>

        <Step expanded completed={Boolean(config?.device_no)}>
          <StepLabel>Connect RentBeta on the URA EFRIS portal</StepLabel>
          <StepContent>
            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                <li>Download your key file below (and the step-by-step guide if you'd like).</li>
                <li>
                  Log in to <strong>efris.ura.go.ug</strong> with your own TIN and EFRIS password.
                </li>
                <li>Choose <strong>System-to-System</strong> integration and upload the key file.</li>
                <li>
                  Once URA approves, copy the <strong>Device Number</strong> shown for that device.
                </li>
              </ol>
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" disabled={!config?.has_certificate} onClick={() => handleDownloadKey("crt")}>
                Download key file (.crt)
              </Button>
              <Button disabled={!config?.has_certificate} onClick={() => handleDownloadKey("cer")}>
                .cer version
              </Button>
              <Button variant="outlined" onClick={handleDownloadInstructions}>
                Step-by-step guide (PDF)
              </Button>
            </Stack>
          </StepContent>
        </Step>

        <Step expanded completed={config?.onboarding_status === "active"}>
          <StepLabel>Enter your TIN and device number</StepLabel>
          <StepContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              RentBeta then checks the connection with URA, picks up your business details and
              VAT registration from URA, and switches on EFRIS receipts.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                label="TIN"
                value={device.tin}
                onChange={(e) => setDevice({ ...device, tin: e.target.value.trim() })}
              />
              <TextField
                size="small"
                label="Device number"
                value={device.device_no}
                onChange={(e) => setDevice({ ...device, device_no: e.target.value.trim() })}
              />
              <Button
                variant="contained"
                disabled={!hasKeys || !device.tin || !device.device_no || busy}
                onClick={handleVerify}
              >
                {busy ? "Connecting…" : "Connect"}
              </Button>
            </Stack>
          </StepContent>
        </Step>
      </Stepper>
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        <strong>VAT:</strong> if URA has you registered for VAT, rent is invoiced with 18% VAT
        included and you can also issue EFRIS invoices for several months at once. Otherwise each
        rent payment gets a VAT-exempt e-receipt.
      </Typography>
    </Paper>
  );
}

function EfrisDocuments({ config: initial }) {
  const { listDocuments, downloadDocumentPdf, saveSettings } = useEinvoicing();
  const [config, setConfig] = useState(initial);
  const [docs, setDocs] = useState(null);
  const [message, setMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    const result = await listDocuments();
    if (result.success) setDocs(result.data.data || []);
    else setMessage({ severity: "error", message: result.message });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleVat = async (event) => {
    const result = await saveSettings({ vat_registered: event.target.checked });
    if (result.success) setConfig(result.data.data);
    setMessage(
      result.success
        ? { severity: "success", message: "VAT setting saved. RentBeta will update your rent product on EFRIS." }
        : { severity: "error", message: result.message }
    );
  };

  const handlePdf = async (doc) => {
    const result = await downloadDocumentPdf(doc);
    if (!result.success) setMessage({ severity: "error", message: result.message });
  };

  const seller = config?.seller_details || {};

  return (
    <Paper sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={1}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            EFRIS receipts & invoices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Issued in your name: {seller.legalName || seller.businessName} · TIN {seller.tin}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label="EFRIS active" color="success" />
          <Button
            variant="contained"
            disabled={!config?.vat_registered}
            onClick={() => setDialogOpen(true)}
          >
            Generate EFRIS invoice
          </Button>
        </Stack>
      </Stack>
      {!config?.vat_registered && (
        <Typography variant="caption" color="text.secondary">
          EFRIS invoices for several months are for VAT-registered landlords. Your rent payments
          get EFRIS e-receipts automatically when your property manager generates them.
        </Typography>
      )}
      <FormControlLabel
        sx={{ mt: 1 }}
        control={<Switch checked={Boolean(config?.vat_registered)} onChange={handleVat} />}
        label="I am VAT-registered (18% VAT included in rent)"
      />
      {message && (
        <Alert severity={message.severity} sx={{ my: 1 }} onClose={() => setMessage(null)}>
          {message.message}
        </Alert>
      )}
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Details</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>FDN</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs === null ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : docs.filter((d) => d.status === "posted").length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>No EFRIS documents yet.</TableCell>
              </TableRow>
            ) : (
              docs
                .filter((d) => d.status === "posted")
                .map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>{moment(doc.issued_at || doc.date_created).format("D MMM YYYY")}</TableCell>
                    <TableCell>{doc.document_kind === "invoice" ? "e-Invoice" : "e-Receipt"}</TableCell>
                    <TableCell>{doc.buyer_name}</TableCell>
                    <TableCell>
                      {doc.period_start
                        ? `${moment(doc.period_start).format("MMM YYYY")} – ${moment(doc.period_end).format("MMM YYYY")} (${doc.months} mo)`
                        : doc.description}
                    </TableCell>
                    <TableCell align="right">{money(doc.amount)}</TableCell>
                    <TableCell>
                      {doc.fiscal_document_number}
                      {doc.credit_note && (
                        <Chip
                          size="small"
                          sx={{ ml: 1 }}
                          color={doc.credit_note.status === "approved" ? "success" : "default"}
                          label={
                            doc.credit_note.status === "approved" && doc.credit_note.credit_note_fdn
                              ? `Credited (${doc.credit_note.credit_note_fdn})`
                              : `Credit note: ${doc.credit_note.status_label}`
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {doc.validation_url && (
                        <Button size="small" onClick={() => window.open(doc.validation_url, "_blank", "noopener,noreferrer")}>
                          URA
                        </Button>
                      )}
                      <Button size="small" onClick={() => handlePdf(doc)}>
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PeriodInvoiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onIssued={load}
      />
    </Paper>
  );
}

function PeriodInvoiceDialog({ open, onClose, onIssued }) {
  const { getTenancies, issuePeriodInvoice } = useEinvoicing();
  const [data, setData] = useState(null);
  const [occupancyId, setOccupancyId] = useState("");
  const [start, setStart] = useState("");
  const [months, setMonths] = useState(1);
  const [rent, setRent] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setIssued(null);
    getTenancies().then((result) => {
      if (result.success) setData(result.data.data);
      else setError(result.message);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const tenancy = useMemo(
    () => (data?.tenancies || []).find((t) => t.occupancy_id === occupancyId),
    [data, occupancyId]
  );

  const pickTenancy = (id) => {
    setOccupancyId(id);
    const t = (data?.tenancies || []).find((x) => x.occupancy_id === id);
    if (t) {
      setStart(moment(t.suggested_start).format("YYYY-MM-DD"));
      setRent(String(Number(t.monthly_rent)));
      setMonths(1);
    }
  };

  const rate = Number(data?.vat_rate || 0.18);
  const gross = (Number(rent) || 0) * (Number(months) || 0);
  const vat = Math.round(((gross * rate) / (1 + rate)) * 100) / 100;
  const end = start ? moment(start).add(Number(months) || 0, "months").subtract(1, "day") : null;
  const valid = tenancy && start && Number(months) >= 1 && Number(months) <= 24 && Number(rent) > 0;

  const handleIssue = async () => {
    setBusy(true);
    setError("");
    const result = await issuePeriodInvoice({
      occupancy_id: occupancyId,
      period_start: start,
      months: Number(months),
    });
    setBusy(false);
    if (result.success) {
      setIssued(result.data.data);
      onIssued?.();
    } else {
      setError(result.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate EFRIS invoice</DialogTitle>
      <DialogContent dividers>
        {issued ? (
          <Alert severity="success">
            Invoice issued in your name. FDN <strong>{issued.fiscal_document_number}</strong>,{" "}
            {money(issued.amount)}.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {data && !data.eligible && <Alert severity="info">{data.reason}</Alert>}
            <Typography variant="body2">
              Bill a tenant for one or more months of rent with an official URA invoice. Payments
              for these months won't get a second EFRIS document.
            </Typography>
            <TextField
              select
              size="small"
              label="Tenant"
              value={occupancyId}
              onChange={(e) => pickTenancy(e.target.value)}
            >
              {(data?.tenancies || []).map((t) => (
                <MenuItem key={t.occupancy_id} value={t.occupancy_id}>
                  {t.tenant_name} · {t.property_name} {t.unit_name}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Start date"
                type="date"
                size="small"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Months"
                type="number"
                size="small"
                value={months}
                inputProps={{ min: 1, max: 24 }}
                onChange={(e) => setMonths(e.target.value)}
                sx={{ width: 110 }}
              />
              <TextField
                label="Monthly rent (VAT incl.)"
                size="small"
                value={rent ? withoutSymbolFormatter(Number(rent)) : ""}
                InputProps={{ readOnly: true }}
                helperText="Your recorded rent for this unit"
              />
            </Stack>
            {tenancy && (
              <Box>
                {end && (
                  <Typography variant="body2">
                    Period: {moment(start).format("D MMM YYYY")} – {end.format("D MMM YYYY")}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {months || 0} × {money(rent)} · VAT {rate * 100}% included: {money(vat)}
                </Typography>
                <Typography variant="h6">Total {money(gross)}</Typography>
              </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{issued ? "Close" : "Cancel"}</Button>
        {!issued && (
          <Button variant="contained" disabled={!valid || busy || !data?.eligible} onClick={handleIssue}>
            {busy ? "Issuing…" : "Issue invoice"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
